import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";

// ============================================================================
// LAZY LOADED PAGE IMPORTS
// ============================================================================
// Code splitting: Each page loads only when needed, improving initial load time
const NotFound = lazy(() => import("./pages/NotFound.jsx"));
const AboutPage = lazy(() => import('./pages/aboutpage/AboutPage.jsx'));
const AlumniPage = lazy(() => import('./pages/alumnipage/AlumniPage.jsx'));
const ContactPage = lazy(() => import('./pages/contactpage/ContactPage.jsx'));
const HomePage = lazy(() => import('./pages/homepage/HomePage.jsx'));
const ProgramsPage = lazy(() => import('./pages/programs/ProgramsPage.jsx'));
const ResourcesPage = lazy(() => import('./pages/resourcespage/ResourcesPage.jsx'));
const ServicesPage = lazy(() => import('./pages/servicespage/ServicesPage.jsx'));
const TeamPage = lazy(() => import('./pages/teampage/TeamPage.jsx'));
const FAQPage = lazy(() => import('./pages/faqpage/FAQPage.jsx'));
const PlacementsPage = lazy(() => import('./pages/placementspage/PlacementsPage.jsx'));

// Empty fallback — no loading spinner shown during lazy chunk loading.
const PageLoader = () => null;

// ============================================================================
// SEO WRAPPER COMPONENT
// ============================================================================
/**
 * SEO-optimized route wrapper with meta tags
 * Provides unique title, description, and Open Graph tags for each page
 * 
 * @param {Object} props
 * @param {string} props.title - Page title for browser tab and search engines
 * @param {string} props.description - Meta description for search results
 * @param {string} props.keywords - SEO keywords (comma-separated)
 * @param {string} props.canonical - Canonical URL to prevent duplicate content
 * @param {JSX.Element} props.children - Page component to render
 */
const SEORoute = ({ title, description, keywords, canonical, children }) => {
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://impactacademycm.netlify.app';
  const fullCanonical = `${siteUrl}${canonical}`;
  
  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{title}</title>
        <meta name="title" content={title} />
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={fullCanonical} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={fullCanonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={fullCanonical} />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:description" content={description} />
        
        {/* Additional SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="author" content="Impact Academy (IMAC)" />
      </Helmet>
      {children}
    </>
  );
};

// ============================================================================
// SEO CONFIGURATION FOR EACH ROUTE
// ============================================================================
const KW_BASE = "Impact Academy, IMAC, Limbe prep school, Cameroon concours preparation, ENS, ENAM, ENSET, FMSB, FET, COT, COLTECH";

const seoConfig = {
  home: {
    title: "Impact Academy (IMAC) | Concours Prep School in Limbe, Cameroon",
    description: "IMAC prepares Cameroonian students for ENS, ENAM, ENSET, FMSB, FET, COT and COLTECH concours. Three centers in Limbe — Mile 4, PYC Half, Isokolo.",
    keywords: `${KW_BASE}, prep school Limbe, concours coaching Cameroon`,
    canonical: "/"
  },
  team: {
    title: "Our Team | Impact Academy Faculty & Leadership",
    description: "Meet the lecturers, mentors and leadership behind Impact Academy — the people preparing Cameroon's next concours laureates.",
    keywords: `${KW_BASE}, IMAC faculty, IMAC staff, IMAC tutors, concours lecturers Cameroon`,
    canonical: "/teampage"
  },
  about: {
    title: "About IMAC | Our Mission, Vision & Story",
    description: "Why Impact Academy exists, what we believe, and how we have built one of Limbe's most trusted concours preparation programs.",
    keywords: `${KW_BASE}, IMAC mission, IMAC vision, IMAC history`,
    canonical: "/aboutpage"
  },
  contact: {
    title: "Contact Impact Academy | WhatsApp, Email & Centers in Limbe",
    description: "Reach Impact Academy on WhatsApp, by email or at our Mile 4, PYC Half and Isokolo centers in Limbe. We respond fast — usually within hours.",
    keywords: `${KW_BASE}, contact IMAC, IMAC WhatsApp, IMAC Limbe address`,
    canonical: "/contactpage"
  },
  alumni: {
    title: "IMAC Alumni | Stories from Cameroon's Top Concours Laureates",
    description: "Read where IMAC graduates are now — universities, careers and the journey from our classroom to ENS, ENAM, ENSET, FMSB and beyond.",
    keywords: `${KW_BASE}, IMAC alumni, ENS laureates, ENAM laureates, FMSB laureates`,
    canonical: "/alumnipage"
  },
  services: {
    title: "Services | Tutoring, Mentorship & Admissions Support — IMAC",
    description: "Individual tutoring, group concours prep, mentorship and admissions consulting for ENS, ENAM, ENSET, FMSB and engineering schools.",
    keywords: `${KW_BASE}, tutoring Limbe, mentorship, admissions consulting Cameroon`,
    canonical: "/servicespage"
  },
  resources: {
    title: "Free Resources | Past Papers, Notes & Concours Guides — IMAC",
    description: "Download past concours papers, revision notes and study guides for ENS, ENAM, ENSET, FMSB and other competitive exams in Cameroon.",
    keywords: `${KW_BASE}, concours past papers, ENS past questions, ENAM past questions, revision notes`,
    canonical: "/resourcespage"
  },
  programs: {
    title: "Concours Programs | ENS, ENAM, ENSET, FMSB, FET, COT, COLTECH — IMAC",
    description: "Explore IMAC's concours preparation tracks: ENS, ENAM, ENSET, FMSB, FET, COT, COLTECH, engineering schools and military schools.",
    keywords: `${KW_BASE}, ENS prep, ENAM prep, ENSET prep, FMSB prep, military schools Cameroon`,
    canonical: "/programs"
  },
  faq: {
    title: "FAQ | Impact Academy (IMAC) — Common Questions Answered",
    description: "Answers to the most common questions about IMAC programs, admissions, fees, scholarships, centers and concours preparation.",
    keywords: `${KW_BASE}, IMAC FAQ, admissions, fees, scholarships`,
    canonical: "/faq"
  },
  placements: {
    title: "Placements | Where IMAC Laureates Land — ENS, ENAM, ENSP, FMSB, FET",
    description: "IMAC graduates have been placed at every major concours-driven school in Cameroon. Browse the full list of partner placements.",
    keywords: `${KW_BASE}, IMAC placements, partner schools, ENS placement, ENAM placement`,
    canonical: "/placements"
  },
  notFound: {
    title: "Page Not Found | Impact Academy (IMAC)",
    description: "We couldn't find that page. Head back to the IMAC homepage to explore our concours programs and services.",
    keywords: "404, not found",
    canonical: "/404"
  }
};

// ============================================================================
// MAIN ROUTES COMPONENT
// ============================================================================
/**
 * Main routing configuration with lazy loading and SEO optimization
 * Features:
 * - Code splitting for faster initial load
 * - SEO meta tags for each route
 * - Error boundary for graceful error handling
 * - Scroll restoration on navigation
 * - Loading states for async components
 * 
 * @returns {JSX.Element} Configured router with all application routes
 */
const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <RouterRoutes>
            {/* ============================================================
                ROOT ROUTES - Homepage accessible via / and /homepage
            ============================================================ */}
            <Route 
              path="/" 
              element={
                <SEORoute {...seoConfig.home}>
                  <HomePage />
                </SEORoute>
              } 
            />
            <Route 
              path="/homepage" 
              element={
                <SEORoute {...seoConfig.home}>
                  <HomePage />
                </SEORoute>
              } 
            />

            {/* ============================================================
                MAIN NAVIGATION ROUTES
            ============================================================ */}
            
            {/* Team page - displays team members/staff */}
            <Route 
              path="/teampage" 
              element={
                <SEORoute {...seoConfig.team}>
                  <TeamPage />
                </SEORoute>
              } 
            />
            
            {/* About page - organization information */}
            <Route 
              path="/aboutpage" 
              element={
                <SEORoute {...seoConfig.about}>
                  <AboutPage />
                </SEORoute>
              } 
            />
            
            {/* Contact page - contact form and information */}
            <Route 
              path="/contactpage" 
              element={
                <SEORoute {...seoConfig.contact}>
                  <ContactPage />
                </SEORoute>
              } 
            />
            
            {/* Alumni page - alumni information and resources */}
            <Route 
              path="/alumnipage" 
              element={
                <SEORoute {...seoConfig.alumni}>
                  <AlumniPage />
                </SEORoute>
              } 
            />
            
            {/* Services page - services offered */}
            <Route 
              path="/servicespage" 
              element={
                <SEORoute {...seoConfig.services}>
                  <ServicesPage />
                </SEORoute>
              } 
            />
            
            {/* Resources page - downloadable resources and links */}
            <Route 
              path="/resourcespage" 
              element={
                <SEORoute {...seoConfig.resources}>
                  <ResourcesPage />
                </SEORoute>
              } 
            />
            
            {/* Programs page - available programs */}
            <Route 
              path="/programs" 
              element={
                <SEORoute {...seoConfig.programs}>
                  <ProgramsPage />
                </SEORoute>
              } 
            />

            {/* FAQ page */}
            <Route
              path="/faq"
              element={
                <SEORoute {...seoConfig.faq}>
                  <FAQPage />
                </SEORoute>
              }
            />

            {/* Placements page */}
            <Route
              path="/placements"
              element={
                <SEORoute {...seoConfig.placements}>
                  <PlacementsPage />
                </SEORoute>
              }
            />

            {/* ============================================================
                FALLBACK ROUTE
            ============================================================ */}
            
            {/* 404 Not Found - catches all unmatched routes */}
            {/* MUST be last route to catch everything else */}
            <Route 
              path="*" 
              element={
                <SEORoute {...seoConfig.notFound}>
                  <NotFound />
                </SEORoute>
              } 
            />
          </RouterRoutes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;